.section,
.motionRoot {
  min-height: 0;
}

.section {
  overflow: clip;
  background: #050505;
}

.stage {
  position: relative;
  width: 100vw;
  height: auto;
  min-height: 0;
  padding: clamp(4rem, 10vh, 7rem) 0 var(--space-xl);
  background: #050505;
  isolation: isolate;
}

.visual {
  position: relative;
  width: 100vw;
  height: 100svh;
  min-height: 40rem;
  overflow: hidden;
  background: #050505;
}

.visual::after {
  position: absolute;
  z-index: var(--z-overlay);
  inset: 0;
  background: radial-gradient(circle at center, transparent 52%, rgb(0 0 0 / 34%));
  content: '';
  pointer-events: none;
}

.scene,
.afterLayer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.scene {
  z-index: var(--z-media);
  object-fit: cover;
  object-position: center bottom;
}

.afterLayer {
  z-index: calc(var(--z-media) + 1);
  overflow: hidden;
  will-change: clip-path;
}

.afterLayer .scene {
  z-index: auto;
}

.slider {
  position: absolute;
  z-index: var(--z-sticky);
  top: 0;
  bottom: 0;
  width: 2.75rem;
  padding: 0;
  color: var(--color-primary);
  cursor: ew-resize;
  touch-action: none;
  transform: translateX(-50%);
}

.sliderLine {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 1px;
  background: var(--color-primary);
  box-shadow: 0 0 12px rgb(255 255 255 / 70%);
}

.sliderHandle {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0.75rem;
  height: 0.75rem;
  border: 2px solid var(--color-primary);
  border-radius: 50%;
  background: rgb(5 5 5 / 55%);
  box-shadow: 0 0 18px rgb(255 255 255 / 75%);
  transform: translate(-50%, -50%);
}

.lanternExhibit {
  position: relative;
  z-index: calc(var(--z-content) + 1);
  width: clamp(20rem, 25vw, 25rem);
  margin: clamp(3rem, 5vw, 4rem) auto clamp(3rem, 5vw, 4rem);
  mix-blend-mode: screen;
  text-align: center;
}

.lanternToken {
  position: relative;
  width: 100%;
  aspect-ratio: 3 / 4;
  overflow: hidden;
  border-radius: 0.375rem;
  background: transparent;
  cursor: pointer;
}

.lanternVideo,
.lanternPoster,
.lanternWechatAnimation {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  background: transparent;
  object-fit: cover;
}

.lanternVideo {
  transition: filter 280ms ease;
}

.lanternWechatAnimation {
  opacity: 0;
  transition: opacity 280ms ease;
}

.lanternWechatAnimationReady {
  opacity: 1;
}

.lanternPlayButton {
  position: absolute;
  z-index: 2;
  inset: 0;
  display: grid;
  width: 100%;
  padding: 0;
  place-items: center;
  color: var(--color-primary);
  cursor: pointer;
  transition:
    opacity 280ms ease,
    transform 280ms ease;
}

.lanternPlayButton::before {
  position: absolute;
  width: 4rem;
  height: 4rem;
  border: 1px solid rgb(255 255 255 / 58%);
  border-radius: 50%;
  background: rgb(5 5 5 / 34%);
  box-shadow: 0 0 2rem rgb(255 255 255 / 10%);
  backdrop-filter: blur(0.35rem);
  content: '';
  transition:
    border-color 280ms ease,
    background-color 280ms ease,
    transform 280ms ease;
}

.lanternPlayButton:hover::before,
.lanternPlayButton:focus-visible::before {
  border-color: rgb(255 255 255 / 85%);
  background: rgb(5 5 5 / 44%);
  transform: scale(1.05);
}

.lanternToken:hover .lanternVideo {
  filter: brightness(1.06);
}

.lanternPlayButtonHidden {
  pointer-events: none;
  opacity: 0;
  transform: scale(1.04);
}

.lanternPlayGlyph {
  position: relative;
  z-index: 1;
  width: 0;
  height: 0;
  margin-left: 0.25rem;
  border-top: 0.55rem solid transparent;
  border-bottom: 0.55rem solid transparent;
  border-left: 0.85rem solid currentcolor;
}

.lanternCaption {
  display: grid;
  margin-top: var(--space-xs);
  gap: 0.3rem;
  justify-items: center;
}

.lanternCaptionTitle {
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  line-height: var(--line-height-body);
}

.lanternCaptionHint {
  color: var(--color-text-secondary);
  font-size: var(--font-size-caption);
  letter-spacing: 0.12em;
  line-height: 1.4;
}

.card {
  position: relative;
  z-index: var(--z-content);
  display: flex;
  width: min(30rem, calc(100vw - 10rem));
  max-width: 30rem;
  margin: 0 auto;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-xs);
  text-align: left;
}

.title {
  margin-top: 0.5rem;
  font-size: clamp(2.5rem, 4vw, var(--font-size-section));
  font-weight: var(--font-weight-semibold);
  letter-spacing: -0.01em;
  line-height: var(--line-height-section);
}

.subtitle {
  color: var(--color-text-primary);
  font-size: clamp(1.125rem, 1.65vw, var(--font-size-subheading));
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-subheading);
}

.body {
  color: var(--color-text-secondary);
  line-height: var(--line-height-body);
}

@media (max-width: 63.9375rem) {
  .section,
  .motionRoot {
    height: auto;
    min-height: 0;
  }

  .stage {
    padding-block: var(--space-l);
    overflow: visible;
  }

  .visual {
    height: auto;
    min-height: 0;
    aspect-ratio: 16 / 9;
  }

  .visual::after {
    background: linear-gradient(
      180deg,
      rgb(5 5 5 / 4%) 0%,
      transparent 62%,
      rgb(5 5 5 / 22%) 100%
    );
  }

  .scene {
    background: #050505;
    object-fit: contain;
    object-position: center;
  }

  .slider {
    display: none;
  }

  .lanternExhibit {
    width: clamp(15.625rem, 35vw, 18.75rem);
    margin: clamp(3rem, 6vw, 4rem) auto;
  }

  .card {
    width: min(calc(100% - (var(--page-margin) * 2)), 48rem);
    max-width: none;
    margin: 0 auto;
    padding: 0;
    background: none;
    border: 0;
    backdrop-filter: none;
    text-align: left;
    text-shadow: none;
  }
}

@media (max-width: 47.9375rem) {
  .stage {
    padding-top: var(--space-m);
    padding-bottom: var(--space-l);
  }

  .lanternExhibit {
    width: clamp(15rem, 65vw, 17.5rem);
    margin: 3rem auto;
  }

  .title {
    font-size: var(--font-size-section);
  }

  .subtitle {
    font-size: var(--font-size-subheading);
  }
}
